import React, { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { toJS } from "mobx";
import ImageSelector from "../../../../../elements/v2/ImageSelector/ImageSelector.jsx";
import InputText from "../../../../../elements/v2/TextInput/TextInput.jsx";
import TextArea from "../../../../../elements/v2/TextArea/TextArea.jsx";
import GenericList from "../../../../../elements/v2/GenericList/GenericList.jsx";
import InstructionsBox from "../../../../../elements/v2/InstructionsBox/InstructionsBox.jsx";
import KnowledgeBaseTable from "../../../../../elements/v2/KnowledgeBaseTable/KnowledgeBaseTable.jsx";
import ActionsList from "../../../../../elements/v2/ActionsCatalog/ActionsList.jsx";
import { useStore } from "../../../../../hooks/index.js";
import { ExpandableComponent } from "../../../../../elements/index.js";
import { useUserCheckPermissions } from "../../../../../hooks/useUserCheckPermissions";
import {
  Title,
  ContainerInnerTab,
  RadioButtonContainer,
} from "../../PlaygroundView.styled.js";
import {
  assistantListFileRoute,
  extensions,
} from "../../../../../utils/enums.js";
import RadioButton from "../../../../../elements/v2/RadioButton/RadioButton.jsx";
import SelectDropdown from "../../../../../elements/v2/SelectDropdown/SelectDropdown.jsx";
import AgentCard from "../../../AgentsList/components/AgentCard";
import { PermissionCodes, PermissionLevels } from "../../../../../utils/enums";

const BasicConfigurationTab = observer(() => {
  const { editAgent } = useStore();
  const [showModal, setShowModal] = useState(false);
  const { checkPermission } = useUserCheckPermissions();

  const onHandleChangeCategory = (value) => {
    editAgent.setSelectedAgentCategory(value);
  };
  
  const onHandleChangePrompt = (value) => {
    editAgent.setSelectedPromptId(value);
  }

  useEffect(() => {
    editAgent.fetchCategories();
  }, []);

  const onHandleChangeName = useCallback(
    (e) => {
      editAgent.onChangeName(e.target.value);
    },
    [editAgent],
  );

  const onChangeConversationStarter = useCallback((idx, value) => {
    editAgent.updateConversationStarter(
      Object.keys(editAgent.conversationStarters)[idx],
      value,
    );
  });

  const onHandleChangeInstructions = useCallback(
    (value) => {
      editAgent.onChangeInstructions(value);
    },
    [editAgent],
  );

  const onHandleChangeTitle = useCallback(
    (e) => {
      editAgent.onChangeTitle(e.target.value);
    },
    [editAgent],
  );

  const handleRadioChange = (value) => {
    editAgent.onChangeIncludeConcierge(value);
  };

  const handleAgentTypeChange = useCallback(
    (value) => {
      editAgent.onAgentTypeChange(value);
    },
    [editAgent],
  );

  const promptsItems = editAgent.prompts.map((item) => {
    return {
      label: item.name,
      value: item.id,
    };
  });
  
  return (
    <ContainerInnerTab>
      {checkPermission(PermissionCodes.BASIC_PICTURE, PermissionLevels.VIEW) && (
      <ImageSelector
        editAgent={editAgent}
        customerId={editAgent.account}
        />
        )}
      {checkPermission(PermissionCodes.BASIC_NAME, PermissionLevels.VIEW) && (
      <span>
        <Title>Name</Title>
        <InputText
          placeholder="Enter name"
          value={editAgent.name}
          onChange={onHandleChangeName}
          displayError={!editAgent.name}
          validationError="Your agent needs a name before saving the changes."
        />
      </span>
      )}

      {checkPermission(PermissionCodes.BASIC_CATEGORY, PermissionLevels.VIEW) && (
      <span>
        <Title>Agent Category</Title>
        <SelectDropdown 
          items={editAgent.agentCategories || []} 
          label="Select Category" 
          onSelect={onHandleChangeCategory}
          selectedItem={editAgent.selectedAgentCategory}
          defaultValue={editAgent.category || null}
        />
      </span>
      )}
      
      {/* NOTE: Description field has been moved to BasicInfoSection component
          See: src/pages/v2/Playground/pages/ConfigPage/components/BasicInfoSection.jsx (lines 26-33)
          Original implementation commented out below: */}
      {/* {checkPermission(PermissionCodes.BASIC_DESCRIPTION, PermissionLevels.VIEW) && (
        <span>
          <Title>Description</Title>
          <TextArea
            height="120px"
            resize="none"
            placeholder="Enter description"
            value={editAgent.title}
            onChange={onHandleChangeTitle}
          />
        </span>
      )} */}
      {
        editAgent.typeAgent === "langchain" && 
        checkPermission(PermissionCodes.BASIC_PROMPT, PermissionLevels.VIEW) && 
        (
          <>
            <span>
              <Title>Instructions</Title>
              <SelectDropdown
                items={promptsItems || []}
                label="Select Prompt"
                onSelect={onHandleChangePrompt}
                selectedItem={editAgent.prompt_id}
                defaultValue={editAgent.prompt_id || null}
              />
            </span>
            <span>
              <TextArea
                height="120px"
                resize="none"
                placeholder="Propmt selected"
                value={editAgent.prompt}
              />
            </span>
          </>
        )
      }

      {
        editAgent.typeAgent !== "langchain" &&
        checkPermission(PermissionCodes.BASIC_INSTRUCTIONS, PermissionLevels.VIEW) &&
        (
          <InstructionsBox
            value={editAgent.instructions}
            onChange={onHandleChangeInstructions}
            toggleModal={() => setShowModal(true)}
            instructionsHelper={!editAgent.isConcierge}
          />
        )
      }
      
      {checkPermission(PermissionCodes.BASIC_STARTERS, PermissionLevels.VIEW) && (
      <GenericList
        title="Starters"
        listItems={Object.values(editAgent.conversationStarters)}
        setListItems={onChangeConversationStarter}
        newItem={(value) => editAgent.newConversationStarter(value)}
        removeItem={(idx) => editAgent.removeConversationStarter(idx)}
        placeholder={"Type your question starter here"}
      />
      )}

      {checkPermission(PermissionCodes.BASIC_KNOWLEDGE, PermissionLevels.VIEW) && (
      <KnowledgeBaseTable
        fileNames={editAgent.files}
        vaultName={editAgent.account}
        ownerName={editAgent.name}
        onFilesChange={editAgent.onChangeFiles}
        onError={editAgent.onSetError}
        customer={editAgent.account}
        fileRoute={assistantListFileRoute}
        editAgentStore={editAgent}
        extensions={extensions}
      />
     )}

      {!editAgent.isConcierge && checkPermission(PermissionCodes.BASIC_ACTIONS, PermissionLevels.VIEW) && (
      <ActionsList
        agentActions={editAgent.actions}
        onSave={editAgent.onChangeActions}
      />
      )}

      {!editAgent.isConcierge && checkPermission(PermissionCodes.BASIC_CONCIERGE, PermissionLevels.VIEW) && (
        <span>
        <Title>Include agent in concierge?</Title>
        <RadioButtonContainer>
          <RadioButton
            label="Yes"
            value="yes"
            name="includeConcierge"
            onChange={handleRadioChange}
            checked={editAgent.addConcierge}
          />
          <RadioButton
            label="No"
            value="no"
            name="includeConcierge"
            onChange={handleRadioChange}
            checked={!editAgent.addConcierge}
          />
        </RadioButtonContainer>
      </span>
      )}

      {!editAgent.isConcierge && checkPermission(PermissionCodes.BASIC_TYPE, PermissionLevels.VIEW) && (
      <span>
        <Title>Agent type</Title>
        <RadioButtonContainer>
          <RadioButton
            label="Internal"
            value="internal"
            name="internal"
            checked={editAgent.type === "internal"}
            onChange={handleAgentTypeChange}
          />
          <RadioButton
            label="Consumer"
            value="consumer"
            name="consumer"
            checked={editAgent.type === "consumer"}
            onChange={handleAgentTypeChange}
          />
        </RadioButtonContainer>
      </span>
      )}

      {showModal && (
        <ExpandableComponent
          isOpen={showModal}
          title={editAgent.instructions}
          onClose={() => setShowModal(false)}
        />
      )}
    </ContainerInnerTab>
  );
});

export default BasicConfigurationTab;
